import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin, useMe } from '../../api/session';
import { useCrumbs } from '../../layout/crumbs';
import { Button, LinkButton } from '../../ui/Button';
import { ErrorAlert, PageUnavailable } from '../../ui/feedback';
import { PageHeader, usePageTitle } from '../../ui/misc';
import { CandidateForm, toPayload, type CandidateValues } from './CandidateForm';

/** WF-10: admins prepare candidates in the UI; AI services use MCP. */
export function NewReleasePage() {
  const { key } = useParams();
  const project = useProject(key);
  const admin = useIsAdmin();
  const me = useMe();
  const navigate = useNavigate();
  usePageTitle(project.data ? `${project.data.name} · Create release` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Releases', to: `/projects/${key}/releases` },
    { label: 'New release' },
  ]);
  const [v, setV] = useState<CandidateValues>({
    title: '',
    environmentId: '',
    ticketKeys: [],
    sourceRevision: '',
    artifactDigest: '',
    artifactRef: '',
    integrationEvidence: '',
    deploymentPlan: '',
    verificationChecks: [''],
    rollbackPlan: '',
    executor: { kind: 'human', userId: me.user.id },
  });
  const create = useAction(() => api.post<{ releaseId: string }>(`/projects/${key}/releases`, toPayload(v)), {
    success: (r) => `Prepared ${r.releaseId}`,
    invalidate: [
      ['releases', key],
      ['releasable', key],
    ],
    onSuccess: (r) => navigate(`/projects/${key}/releases/${r.releaseId}`),
  });
  if (!admin || project.data?.archived) return <PageUnavailable />;
  return (
    <div className="page narrow">
      <PageHeader
        title="Create release"
        subtitle="A draft candidate does not deploy anything. You'll request approval for its exact binding next."
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(undefined);
        }}
      >
        <CandidateForm projectKey={key!} value={v} onChange={setV} />
        <ErrorAlert error={create.error} />
        <div className="form-actions">
          <LinkButton to={`/projects/${key}/releases`}>Cancel</LinkButton>
          <Button type="submit" loading={create.isPending} disabled={!v.ticketKeys.length}>
            Create release candidate
          </Button>
        </div>
      </form>
    </div>
  );
}
